import DOMPurify from "dompurify";
import TextWithLinks from "./TextWithLinks";

// Renders a stored body in whichever format it was saved as (MGDWD-148).
//
// Two formats coexist permanently. Everything written before the editor is
// plain text and is escaped by React via TextWithLinks; bodies written in the
// editor are HTML and are sanitised here. The format is read from the row, not
// guessed from the content — real notes contain prose like "<sigh>" that a
// sanitiser would silently eat if we tried to detect markup.

// Deliberately narrower than what the editor's toolbar can produce is not the
// goal — this is exactly what it produces, and nothing else gets through.
const ALLOWED_TAGS = ["b", "strong", "i", "em", "u", "s", "a", "p", "br", "ul", "ol", "li"];
const ALLOWED_ATTR = ["href", "target", "rel"];

const URL_PATTERN = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

const SENTENCE_PUNCTUATION = ".,;:!?\"'";
const CLOSERS = { ")": "(", "]": "[", "}": "{" };

// Same rule as TextWithLinks: a URL ending a sentence must not swallow the
// punctuation, but a bracket the URL itself opened stays part of it.
function splitTrailingPunctuation(raw) {
	let end = raw.length;
	while (end > 0) {
		const char = raw[end - 1];
		if (SENTENCE_PUNCTUATION.includes(char)) {
			end -= 1;
			continue;
		}
		const opener = CLOSERS[char];
		if (opener) {
			const body = raw.slice(0, end);
			if (body.split(char).length - 1 > body.split(opener).length - 1) {
				end -= 1;
				continue;
			}
		}
		break;
	}
	return [raw.slice(0, end), raw.slice(end)];
}

// The editor only creates a link when the author presses the link button, so a
// URL merely typed into a comment still arrives as a text node. Walking the
// parsed DOM rather than the HTML string means hrefs, class names and every
// other attribute are structurally out of reach, and text already inside an
// <a> is skipped so anchors never nest.
function linkifyTextNodes(root) {
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
	const targets = [];
	while (walker.nextNode()) {
		const node = walker.currentNode;
		if (node.parentElement && node.parentElement.closest("a")) {
			continue;
		}
		URL_PATTERN.lastIndex = 0;
		if (URL_PATTERN.test(node.nodeValue)) {
			targets.push(node);
		}
	}

	targets.forEach((node) => {
		const parts = node.nodeValue.split(URL_PATTERN);
		const fragment = document.createDocumentFragment();
		parts.forEach((part, index) => {
			if (index % 2 === 0) {
				if (part) {
					fragment.appendChild(document.createTextNode(part));
				}
				return;
			}
			const [url, tail] = splitTrailingPunctuation(part);
			const anchor = document.createElement("a");
			anchor.href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
			anchor.textContent = url;
			fragment.appendChild(anchor);
			if (tail) {
				fragment.appendChild(document.createTextNode(tail));
			}
		});
		node.parentNode.replaceChild(fragment, node);
	});
}

export function renderRichHtml(html) {
	const clean = DOMPurify.sanitize(String(html), {
		ALLOWED_TAGS,
		ALLOWED_ATTR,
	});
	const holder = document.createElement("div");
	holder.innerHTML = clean;
	linkifyTextNodes(holder);
	// Every link, whether the author made it or we did, opens in a new tab and
	// cannot reach window.opener.
	holder.querySelectorAll("a").forEach((anchor) => {
		anchor.setAttribute("target", "_blank");
		anchor.setAttribute("rel", "noopener noreferrer");
	});
	return holder.innerHTML;
}

function RichText({ text, format }) {
	if (!text) {
		return null;
	}

	if (format !== "html") {
		return <TextWithLinks text={text} />;
	}

	return (
		<div
			className="rich-text"
			// Bodies here sit inside rows that navigate on click; a click on a
			// link is never a click on whatever contains it. dangerouslySetInnerHTML
			// rules out an inline handler, so the container catches it instead.
			onClick={(event) => {
				if (event.target.closest && event.target.closest("a")) {
					event.stopPropagation();
				}
			}}
			// eslint-disable-next-line react/no-danger
			dangerouslySetInnerHTML={{ __html: renderRichHtml(text) }}
		/>
	);
}

export default RichText;
